"use client"

import { useState, useEffect } from "react"
import { collection, query, orderBy, getDocs, limit } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { motion } from "framer-motion"
import { MessageSquareQuote, Loader2, Star, User } from "lucide-react"

export default function StudentReviews() {
  const [reviews, setReviews] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const q = query(collection(db, "testimonials"), orderBy("createdAt", "desc"), limit(6))
        const snapshot = await getDocs(q)
        setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchReviews()
  }, [])

  if (!loading && reviews.length === 0) return null

  return (
    <section className="bg-white py-12 sm:py-20 border-t border-slate-100 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 sm:mb-14 gap-6">
           <motion.div 
             initial={{ opacity: 0, x: -20 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             className="space-y-4 max-w-2xl"
           >
             <div className="flex items-center gap-3">
                <div className="w-8 h-px bg-[#b48c36]/40" />
                <p className="text-[#b48c36] font-bold text-[9px] uppercase tracking-[0.4em]">Student Success</p>
             </div>
             <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-950 leading-[1.1]">
                What Our Students <br className="hidden md:block"/> <span className=" font-light text-slate-500">Say About Us.</span>
             </h2>
           </motion.div>
           <motion.div 
             initial={{ opacity: 0, y: 10 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             className="flex flex-col gap-1"
           >
              <div className="flex items-center gap-1 text-[#b48c36]">
                  {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
              </div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">4.9/5 Average Merit Rating</p>
           </motion.div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {loading ? (
             <div className="col-span-full py-20 flex justify-center"><Loader2 className="animate-spin text-[#b48c36]" /></div>
          ) : (
            reviews.map((review, i) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative bg-[#fcfaf7] border border-slate-100 p-6 sm:p-7 pt-10 sm:pt-11 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl"
              >
                {/* Floating Category Tag */}
                <span className={`absolute top-0 right-6 -translate-y-1/2 text-[7px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${review.category === 'Success Story' ? 'bg-[#b48c36] text-white shadow-md' : 'bg-slate-900 text-[#b48c36]'}`}>
                   {review.category || 'Feedback'}
                </span>

                {/* Profile row */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-white shadow-md ring-1 ring-slate-100">
                    {review.imageUrl ? (
                      <img src={review.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt="" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#b48c36] font-serif font-bold italic bg-slate-50 text-sm">
                        {review.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-slate-900 leading-tight text-sm">{review.name}</h4>
                    <p className="text-[9px] text-[#b48c36] font-bold uppercase tracking-widest mt-0.5">{review.designation}</p>
                  </div>
                </div>

                {/* Rating & Quote Content */}
                <div className="relative">
                  <div className="flex gap-0.5 mb-3">
                     {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={10} className={star <= (review.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
                     ))}
                  </div>
                  <MessageSquareQuote size={18} className="absolute -top-3 -left-1.5 text-[#b48c36]/10" />
                  <p className="text-slate-600 text-[13px] leading-relaxed italic font-serif z-10 relative">
                    "{review.content}"
                  </p>
                </div>

                {/* Status Bar */}
                <div className="mt-6 pt-5 border-t border-slate-200/50 flex items-center justify-between">
                   <div className="flex items-center gap-1.5">
                      <div className="w-1 h-1 bg-emerald-500 rounded-full" />
                      <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Verified Opinion</span>
                   </div>
                   <button className="text-[8px] font-bold text-[#b48c36] uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">Read Story</button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  )
}
